import { supabase } from '../supabase'; // Assuming supabase import is needed or used by BaseRecommender/utils
import { Game, RecommendationFilters } from '../../types/index'; // Importar Game e Filters de index
import { BaseRecommender } from './baseRecommender';
import { DatabaseUtils } from './utils/databaseUtils';
import { GameProcessingUtils } from './utils/gameProcessingUtils';
import { RecommendationDataService } from './services/recommendationDataService';
import { RECOMMENDATION_CONFIG } from './index'; // IMPORTAR A CONFIG

/**
 * Recomendador baseado em conteúdo que utiliza categorias, mecânicas e temas
 * dos jogos que o usuário gosta para gerar recomendações similares
 */
export class ContentBasedRecommender extends BaseRecommender {
  /**
   * Obtém recomendações baseadas nos jogos que o usuário gosta
   */
  public async getRecommendations(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: RecommendationFilters
  ): Promise<Game[]> {
    try {
      // Validar IDs de exclusão e adicionar jogos já possuídos/não gostados
      const validExcludeIds = await this.getCompleteExcludeIds(userId, excludeGameIds);
      
      // Obter preferências do usuário a partir do serviço centralizado
      const userPreferences = await RecommendationDataService.getUserPreferences(userId);
      
      if (userPreferences.categories.size === 0) {
        console.warn('Usuário não tem preferências claras para recomendações baseadas em conteúdo');
        return this.getFallbackRecommendations(count, validExcludeIds);
      }

      // Aplicar filtros baseados nas preferências do usuário (isto já busca as preferências)
      const enhancedFilters = await this.applyDefaultFilters(userId, filters);
      
      // Log para diagnóstico
      console.log(`Buscando jogos com categorias similares: ${Array.from(userPreferences.categories).join(', ')}`);
      console.log(`[ContentBased DEBUG] Filtros a serem aplicados na query:`, enhancedFilters);
      console.log(`Excluindo ${validExcludeIds.length} IDs`);
      
      // Buscar jogos aplicando TODOS os filtros (categorias + enhancedFilters) diretamente no DB
      let similarGames = await DatabaseUtils.getGamesBySimilarCategories(
        Array.from(userPreferences.categories),
        validExcludeIds,
        enhancedFilters, // <-- PASSAR FILTROS AQUI
        count * 3 // Buscar mais para ter margem, a ordenação final selecionará os melhores
      );
      
      // Log ANTES da filtragem manual (que será removida)
      console.log(`[ContentBased DEBUG] Encontrados ${similarGames.length} jogos após busca no DB com filtros`);

      // Verificar se a condição de fallback é atingida (ainda necessária)
      if (similarGames.length < count) {
        console.warn(`[ContentBased WARN] Condição de fallback atingida após busca no DB: ${similarGames.length} < ${count}. Buscando mais opções via fallback.`);
        // A chamada de fallback existente permanece
        return this.getSimpleRecommendations(count, validExcludeIds, {}); // Retorna Game[]
      }
      
      // Ordenar por similaridade
      const sortedGames = GameProcessingUtils.sortGamesBySimilarity(
        similarGames,
        userPreferences.categories,
        userPreferences.mechanics,
        userPreferences.themes
      );
      
      return sortedGames.slice(0, count); // Retorna Game[]
    } catch (error) {
      console.error('Erro ao gerar recomendações baseadas em conteúdo:', error);
      return this.getFallbackRecommendations(count, excludeGameIds); // Retorna Game[]
    }
  }

  /**
   * Método simples para quando a recomendação principal falha
   */
  private async getSimpleRecommendations(
    count: number, 
    excludeGameIds: string[],
    filters?: RecommendationFilters
  ): Promise<Game[]> {
    try {
      console.log('Usando método simplificado de recomendação');
      
      // Buscar jogos bem avaliados (retorna Game[])
      const queryFilters = {
        ...filters,
        'gte_average_rating': RECOMMENDATION_CONFIG.recommenderThresholds.minRatingForSimpleContentFallback
      };

      const highRatedGames = await DatabaseUtils.executeSafeQuery(
        queryFilters,
        count * 3,
        'average_rating',
        false // descendente
      );
      
      // Filtrar IDs excluídos (preserva Game[])
      const filteredGames = GameProcessingUtils.filterExcludedGames(highRatedGames, excludeGameIds);
      
      // Se não temos jogos suficientes, tentar o método de fallback (retorna Game[])
      if (filteredGames.length < count) {
        return this.getFallbackRecommendations(count, excludeGameIds);
      }
      
      // Embaralhar para variedade e retornar (preserva Game[])
      return GameProcessingUtils.shuffleArray(filteredGames).slice(0, count);
    } catch (error) {
      console.error('Erro em recomendação simples:', error);
      return this.getFallbackRecommendations(count, excludeGameIds); // Retorna Game[]
    }
  }
} 